import React, { useState } from 'react';
import { Check, ArrowRight, ArrowLeft, MapPin, Phone, Mail } from 'lucide-react';
import { formOptions, companyInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    serviceArea: '',
    message: '',
    lgpdConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.company && formData.phone && formData.email;
      case 2:
        return formData.serviceArea && formData.message && formData.lgpdConsent;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    } else {
      toast.error('Preencha todos os campos obrigatórios');
    }
  };

  const prevStep = () => setStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(2)) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Mensagem enviada com sucesso!');
  };

  if (isSubmitted) {
    return (
      <main className="pt-20 min-h-screen bg-[#F7FAFC]">
        <div className="container-main section-spacing">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#3E6B2A] rounded-full flex items-center justify-center mx-auto mb-8">
              <Check size={40} className="text-white" />
            </div>
            <h1 className="heading-2 mb-4">Mensagem enviada!</h1>
            <p className="body-large mb-8">
              Obrigado pelo seu contato. Nossa equipe analisará sua solicitação 
              e retornará em breve.
            </p>
            <a
              href={`https://wa.me/${companyInfo.contact.whatsapp}?text=${encodeURIComponent('Olá! Acabei de enviar uma solicitação pelo site e gostaria de falar com um especialista.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Falar no WhatsApp
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen bg-white">
      {/* Hero */}
      <section className="py-16 bg-[#F7FAFC]">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="caption mb-4 block">Contato</span>
            <h1 className="heading-display mb-6">
              Vamos conversar sobre o seu negócio
            </h1>
            <p className="body-large">
              Solicite uma proposta personalizada e descubra como podemos ajudar 
              sua empresa a alcançar melhores resultados.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="heading-3 mb-8">Informações de contato</h2>
              
              <div className="space-y-6">
                <a 
                  href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.location.address + ', ' + companyInfo.location.city)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-11 h-11 bg-[#E7F2E3] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4E8CD] transition-colors">
                    <MapPin size={20} className="text-[#3E6B2A]" />
                  </div>
                  <div>
                    <p className="text-[#0F172A] font-medium mb-1 group-hover:text-[#3E6B2A] transition-colors">Endereço</p>
                    <p className="text-[#64748B] text-sm">
                      {companyInfo.location.address}<br />
                      {companyInfo.location.neighborhood}<br />
                      {companyInfo.location.city}-{companyInfo.location.state}<br />
                      CEP {companyInfo.location.cep}
                    </p>
                  </div>
                </a>

                <a 
                  href={`https://wa.me/${companyInfo.contact.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-11 h-11 bg-[#E7F2E3] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4E8CD] transition-colors">
                    <Phone size={20} className="text-[#3E6B2A]" />
                  </div>
                  <div>
                    <p className="text-[#0F172A] font-medium mb-1 group-hover:text-[#3E6B2A] transition-colors">Telefone / WhatsApp</p>
                    <p className="text-[#64748B] text-sm">{companyInfo.contact.phone}</p>
                  </div>
                </a>

                <a 
                  href={`mailto:${companyInfo.contact.email}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-11 h-11 bg-[#E7F2E3] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4E8CD] transition-colors">
                    <Mail size={20} className="text-[#3E6B2A]" />
                  </div>
                  <div>
                    <p className="text-[#0F172A] font-medium mb-1 group-hover:text-[#3E6B2A] transition-colors">E-mail</p>
                    <p className="text-[#64748B] text-sm">{companyInfo.contact.email}</p>
                  </div>
                </a>
              </div>

              {/* Map placeholder */}
              <div className="mt-10 rounded-lg overflow-hidden border border-[#E2E8F0]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3984.0441455556377!2d-60.01698!3d-3.1009!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x926c1bc8b8b8b8b8%3A0x8b8b8b8b8b8b8b8b!2sVieiralves%2C%20Manaus%20-%20AM!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                  width="100%"
                  height="180"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização eLauria"
                  className="grayscale-[30%] opacity-90 hover:grayscale-0 hover:opacity-100 transition-all"
                />
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {/* Step Indicator */}
              <div className="flex justify-center mb-10">
                <div className="flex items-center gap-2">
                  {[1, 2].map((s) => (
                    <React.Fragment key={s}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm transition-colors ${
                        step === s 
                          ? 'bg-[#3E6B2A] text-white' 
                          : step > s 
                            ? 'bg-[#3E6B2A] text-white' 
                            : 'bg-[#E2E8F0] text-[#64748B]'
                      }`}>
                        {step > s ? <Check size={16} /> : s}
                      </div>
                      {s < 2 && (
                        <div className={`w-12 h-0.5 ${
                          step > s ? 'bg-[#3E6B2A]' : 'bg-[#E2E8F0]'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="bg-[#F7FAFC] rounded-xl p-8 md:p-10 border border-[#E2E8F0]">
                {/* Step 1: Contact Info */}
                {step === 1 && (
                  <div className="space-y-6">
                    <h2 className="heading-3 mb-6">Seus dados</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label">Nome completo *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Seu nome"
                          required
                        />
                      </div>

                      <div>
                        <label className="form-label">Empresa *</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Nome da empresa"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label">Telefone/WhatsApp *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="(92) 99999-9999"
                          required
                        />
                      </div>

                      <div>
                        <label className="form-label">E-mail *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="seu@email.com"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Service Details */}
                {step === 2 && (
                  <div className="space-y-6">
                    <h2 className="heading-3 mb-6">Sua necessidade</h2>
                    
                    <div>
                      <label className="form-label">Área de interesse *</label>
                      <select
                        name="serviceArea"
                        value={formData.serviceArea}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        <option value="">Selecione</option>
                        {formOptions.serviceAreas.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="form-label">Como podemos ajudar? *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="form-input min-h-[150px] resize-none"
                        placeholder="Descreva seu contexto e urgência. Quanto mais detalhes, melhor poderemos ajudá-lo."
                        required
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="lgpdConsent"
                        name="lgpdConsent"
                        checked={formData.lgpdConsent}
                        onChange={handleChange}
                        className="w-5 h-5 mt-0.5 rounded border-[#E2E8F0] text-[#3E6B2A] focus:ring-[#3E6B2A]"
                        required
                      />
                      <label htmlFor="lgpdConsent" className="text-[#64748B] text-sm">
                        Concordo com o tratamento dos meus dados pessoais conforme a 
                        <a href="/privacidade" className="text-[#3E6B2A] hover:underline"> Política de Privacidade</a> 
                        da eLauria (LGPD). *
                      </label>
                    </div>

                    <p className="text-[#94A3B8] text-xs">
                      Suas informações são confidenciais e não serão compartilhadas com terceiros.
                    </p>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-[#E2E8F0]">
                  {step > 1 ? (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] transition-colors font-medium"
                    >
                      <ArrowLeft size={18} />
                      Voltar
                    </button>
                  ) : (
                    <div />
                  )}

                  {step < 2 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="btn-primary"
                    >
                      Continuar
                      <ArrowRight size={18} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
                      {!isSubmitting && <ArrowRight size={18} />}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
